// <coverflow-slider> — a dependency-free 3D coverflow / carousel.
// Each slide is absolutely centered in the track; JS writes a per-slide 3D transform derived
// from its distance d = index - current (current is fractional while dragging). Pointer drag
// with velocity tracking runs a momentum → snap animation in a single rAF loop. Swappable
// EFFECTS (coverflow / flat-peek / fade / cube), arrows, dots, thumbnails, keyboard + ARIA,
// autoplay with a progress bar, and an optional infinite loop implemented with modular wrap
// distance (no clones): a slide crosses the ±n/2 boundary only when it is off-screen / faded,
// so the wrap is invisible and there is no boundary index-jump to mis-manage.
// Premium (subtle, mostly opt-in): cursor tilt on the active card, drag parallax on the image,
// Ken Burns on the idle active slide, caption reveal. Everything animates transform/opacity only
// and honours prefers-reduced-motion.

const EFFECTS = {
  // classic coverflow: translate + recede in Z + rotateY toward the center + scale down
  coverflow(d, c, dir) {
    const ad = Math.abs(d);
    const tx = d * c.step * dir;
    const tz = -Math.min(ad, c.maxDepth) * c.zStep;
    const ry = -Math.sign(d) * Math.min(ad, 1) * c.angle * dir;
    const sc = Math.max(c.minScale, 1 - ad * c.scaleStep);
    const op = Math.max(c.minOpacity, 1 - ad * c.opacityStep);
    return { t: `translate3d(${tx.toFixed(2)}px,0,${tz.toFixed(2)}px) rotateY(${ry.toFixed(3)}deg) scale(${sc.toFixed(4)})`, op };
  },
  // flat peek: a plain row that slides, neighbours slightly smaller, no rotation/depth
  'flat-peek'(d, c, dir) {
    const ad = Math.abs(d);
    const tx = d * c.step * dir;
    const sc = Math.max(c.minScale, 1 - ad * c.scaleStep);
    const op = Math.max(c.minOpacity, 1 - ad * c.opacityStep);
    return { t: `translate3d(${tx.toFixed(2)}px,0,0) scale(${sc.toFixed(4)})`, op };
  },
  // fade stack: slides sit on top of one another, only opacity separates them
  fade(d, c) {
    const ad = Math.abs(d);
    const sc = Math.max(c.minScale, 1 - ad * c.scaleStep * 0.4);
    const step = c.opacityStep > 0 ? c.opacityStep : 0.85;
    const op = ad < 0.5 ? 1 : Math.max(c.minOpacity, 1 - ad * step);
    return { t: `translate3d(0,0,0) scale(${sc.toFixed(4)})`, op };
  },
  // cube: rotate each face around the track center; the track carries a -halfW compensation
  cube(d, c, dir) {
    const ad = Math.abs(d);
    const ry = -d * 90 * dir;
    return { t: `rotateY(${ry.toFixed(3)}deg) translateZ(${c.halfW.toFixed(2)}px)`, op: ad > 1.15 ? 0 : 1 };
  },
};

class CoverflowSlider extends HTMLElement {
  connectedCallback() {
    if (this._init) {
      // editor drag-move (same element re-connected): re-arm what disconnect tears down
      if (this.n && this.refs && this.refs.track) {
        this._bindWinDoc();
        this._measure();
        this._render();
        this._startAutoplay();
      }
      return;
    }
    this._init = true;

    this.cfg = this._readJSON('[data-cf-config]') || {};
    this.slides = Array.from(this.querySelectorAll('[data-cf-slide]'));
    this.n = this.slides.length;

    this.refs = {
      viewport: this.querySelector('[data-cf-viewport]'),
      stage: this.querySelector('[data-cf-stage]'),
      track: this.querySelector('[data-cf-track]'),
      status: this.querySelector('[data-cf-status]'),
      dots: this.querySelector('[data-cf-dots]'),
      thumbs: this.querySelector('[data-cf-thumbs]'),
      progress: this.querySelector('[data-cf-progress]'),
    };
    this.prevBtns = Array.from(this.querySelectorAll('[data-cf-prev]'));
    this.nextBtns = Array.from(this.querySelectorAll('[data-cf-next]'));

    if (this.n === 0 || !this.refs.track) return;

    // ---- environment ----
    this.rmq = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileq = window.matchMedia('(max-width: 749px)');
    this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.rtl = (this.getAttribute('dir') || document.dir || '').toLowerCase() === 'rtl' || !!this.cfg.rtl;
    this.dirSign = this.rtl ? -1 : 1;

    // infinite loop needs >= 3 real slides
    this.loop = !!this.cfg.loop && this.n >= 3;

    // ---- state ----
    this.current = 0;
    this.target = 0;
    this.velocity = 0;
    this.mode = 'idle'; // idle | drag | momentum | snap
    this.raf = 0;
    this.renderRaf = 0;
    this.activeIndex = 0;
    this._pauseReasons = new Set();

    // clamp start index into range
    const start = Math.min(Math.max(parseInt(this.cfg.startIndex, 10) || 0, 0), this.n - 1);
    this.current = this.target = this.activeIndex = start;

    this.setAttribute('data-cf-ready', '');
    if (this.cfg.effect === 'cube') this.refs.track.classList.add('cf-track--cube');

    this._prepareSlides();
    this._buildDots();
    this._buildThumbs();
    this._measure();
    this._upgradeImages();
    this._bind();
    this._render();
    this._syncControls();
    this._announce();
    this._startAutoplay();
  }

  disconnectedCallback() {
    this._stopAnim();
    if (this.renderRaf) { cancelAnimationFrame(this.renderRaf); this.renderRaf = 0; }
    this._teardownAutoplay();
    if (this._ro) { this._ro.disconnect(); this._ro = null; }
    clearTimeout(this._rt);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this._onVis) document.removeEventListener('visibilitychange', this._onVis);
    this._winDocBound = false;
  }

  // ---------- setup ----------
  _readJSON(sel) {
    const el = this.querySelector(sel);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  _prepareSlides() {
    this.slides.forEach((slide, i) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `${i + 1} / ${this.n}`);
      slide.style.zIndex = String(this.n - i);
      slide._media = slide.querySelector('[data-cf-parallax]');
      slide._tilt = slide.querySelector('[data-cf-tilt]');
      slide._video = slide.querySelector('[data-cf-video]');
      // image settle fade: .is-loaded lifts the [data-cf-rendered] opacity-0 gate in CSS.
      // A failed load must reveal too (error listener + decode catch) — never hold opacity 0.
      const img = slide.querySelector('img.cf-slide__img');
      if (img) {
        const mark = () => img.classList.add('is-loaded');
        if (img.complete) {
          // loaded OR already-failed (cached 404: complete with naturalWidth 0) — reveal either way
          mark();
        } else {
          img.addEventListener('load', mark, { once: true });
          img.addEventListener('error', mark, { once: true });
          if (img.decode) img.decode().then(mark).catch(() => {});
        }
      }
      // click a non-active slide to bring it forward (ignored if it was a drag)
      slide.addEventListener('click', (e) => {
        if (this._moved) { e.preventDefault(); e.stopPropagation(); return; }
        // an EXPLICIT press on a link/button always wins — never hijack it into slide-focusing
        // (side-slide buttons were being swallowed by the bring-forward behaviour below)
        if (e.target.closest('a,button')) return;
        if (i !== this.activeIndex) { e.preventDefault(); this._goTo(i, true, true); }
      });
    });
  }

  _buildDots() {
    const wrap = this.refs.dots;
    if (!wrap) return;
    this.dots = [];
    for (let i = 0; i < this.n; i++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'cf-dot';
      b.setAttribute('aria-label', `${this.cfg.slideWord || 'שקופית'} ${i + 1}`);
      b.addEventListener('click', () => this._goTo(i, true, true));
      wrap.appendChild(b);
      this.dots.push(b);
    }
  }

  _buildThumbs() {
    const wrap = this.refs.thumbs;
    if (!wrap) return;
    this.thumbs = [];
    this.slides.forEach((slide, i) => {
      const src = slide.getAttribute('data-cf-thumb');
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'cf-thumb';
      b.setAttribute('aria-label', `${this.cfg.slideWord || 'שקופית'} ${i + 1}`);
      if (src) {
        const img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        b.appendChild(img);
      }
      b.addEventListener('click', () => this._goTo(i, true, true));
      wrap.appendChild(b);
      this.thumbs.push(b);
    });
  }

  // eager-upgrade the center + neighbours so they are ready before being dragged into view
  _upgradeImages() {
    const upgrade = (i, priority) => {
      const s = this.slides[i];
      if (!s) return;
      s.querySelectorAll('img').forEach((img) => {
        img.loading = 'eager';
        if (priority) { try { img.fetchPriority = 'high'; } catch (e) {} }
      });
    };
    if (this.cfg.lazy === false) {
      for (let i = 0; i < this.n; i++) upgrade(i, i === this.activeIndex);
      return;
    }
    const c = this.activeIndex;
    upgrade(c, true);
    for (let k = 1; k <= 2; k++) {
      upgrade(this._wrapIndex(c + k), false);
      upgrade(this._wrapIndex(c - k), false);
    }
  }

  _wrapIndex(i) {
    return ((i % this.n) + this.n) % this.n;
  }

  // read the live geometry (track width, breakpoint, slides-per-view) into the config object
  _measure() {
    const mobile = this.mobileq.matches;
    const spv = Math.max(1, mobile ? (+this.cfg.spvMobile || 1.5) : (+this.cfg.spvDesktop || 4));
    const w = this.refs.track.clientWidth || this.offsetWidth || 1;
    this.slideW = w / spv;
    this.geom = {
      effect: this.cfg.effect || 'coverflow',
      step: Math.max(1, this.slideW * (+this.cfg.spacing || 0.7) + (+this.cfg.gap || 0)),
      angle: +this.cfg.angle || 0,
      zStep: +this.cfg.zStep || 100,
      maxDepth: +this.cfg.maxDepth || 2,
      scaleStep: +this.cfg.scaleStep || 0,
      minScale: +this.cfg.minScale || 0.8,
      opacityStep: +this.cfg.opacityStep || 0,
      minOpacity: this.cfg.minOpacity != null ? +this.cfg.minOpacity : 0.3,
      halfW: this.slideW / 2,
    };
    // cube: push the whole track back by half a face so the front face sits at z = 0
    this.refs.track.style.setProperty('--cf-half-w', `${this.geom.halfW}px`);
  }

  // ---------- rendering ----------
  _render() {
    const g = this.geom;
    const fn = EFFECTS[g.effect] || EFFECTS.coverflow;
    const half = this.n / 2;
    const fadeBand = Math.min(0.6, half * 0.6);
    const active = this._wrapIndex(Math.round(this.current));
    const parallaxOn = this.cfg.parallax && !this.rmq.matches;
    const dragFrac = this.current - Math.round(this.current); // -0.5..0.5

    for (let i = 0; i < this.n; i++) {
      let d = i - this.current;
      if (this.loop) d -= this.n * Math.round(d / this.n); // nearest wrapped distance

      const r = fn(d, g, this.dirSign);
      let op = r.op;
      // fade the wrap boundary so the modular jump at ±n/2 is never visible
      if (this.loop) {
        const edge = Math.min(1, Math.max(0, (half - Math.abs(d)) / fadeBand));
        op *= edge;
      }

      const slide = this.slides[i];
      slide.style.transform = r.t;
      slide.style.opacity = op.toFixed(3);
      slide.style.zIndex = String(1000 - Math.round(Math.abs(d) * 100));
      slide.style.pointerEvents = op < 0.06 ? 'none' : '';

      // drag parallax: shift the image opposite to the slide's own offset
      if (slide._media) {
        const px = parallaxOn ? -Math.max(-1, Math.min(1, d)) * (+this.cfg.parallaxAmount || 0) * this.dirSign : 0;
        const psc = parallaxOn ? 1.08 : 1;
        slide._media.style.transform = `translate3d(${px.toFixed(2)}px,0,0) scale(${psc})`;
      }

      const isActive = i === active;
      if (isActive !== slide._active) {
        slide._active = isActive;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        slide.querySelectorAll('a,button').forEach((el) => {
          if (isActive) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', '-1');
        });
        this._toggleVideo(slide, isActive);
      }
    }

    if (active !== this.activeIndex) {
      this.activeIndex = active;
      this._syncControls();
      this._announce();
    }
    // suppress caption reveal / ken burns while the user is actively moving slides
    this.refs.track.classList.toggle('cf-track--moving', Math.abs(dragFrac) > 0.02 || this.mode === 'drag');

    // first render done — transforms are written, so lift the pre-JS "only first slide
    // visible" CSS gate. NOT data-cf-ready: that is set BEFORE transforms exist and would
    // reintroduce the stacked-overlap flash frame.
    if (!this.hasAttribute('data-cf-rendered')) this.setAttribute('data-cf-rendered', '');
  }

  _scheduleRender() {
    if (this.renderRaf) return;
    this.renderRaf = requestAnimationFrame(() => {
      this.renderRaf = 0;
      this._render();
    });
  }

  _syncControls() {
    if (this.dots) this.dots.forEach((b, i) => {
      const on = i === this.activeIndex;
      b.classList.toggle('is-active', on);
      if (on) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current');
    });
    if (this.thumbs) this.thumbs.forEach((b, i) => {
      const on = i === this.activeIndex;
      b.classList.toggle('is-active', on);
      if (on) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current');
    });
    if (!this.loop) {
      const atStart = this.activeIndex === 0;
      const atEnd = this.activeIndex === this.n - 1;
      this.prevBtns.forEach((b) => { b.disabled = atStart; });
      this.nextBtns.forEach((b) => { b.disabled = atEnd; });
    }
  }

  _announce() {
    if (this.refs.status) {
      this.refs.status.textContent = `${this.activeIndex + 1} ${this.cfg.ofWord || 'מתוך'} ${this.n}`;
    }
  }

  _toggleVideo(slide, on) {
    const v = slide._video;
    if (!v) return;
    if (on && !this.rmq.matches) {
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    } else {
      v.pause();
    }
  }

  // ---------- navigation ----------
  _next() { this._goTo(this.target + 1, true); }
  _prev() { this._goTo(this.target - 1, true); }

  // `nearest`: treat `index` as an absolute 0..n-1 slide index (dots, thumbs, slide-click,
  // Home/End) and, in loop mode, pick the shortest signed path around the ring from the
  // current fractional position — instead of animating through every slide in between.
  // _next()/_prev() don't pass this: they already hand in a small ±1 step off the running
  // (unbounded) target counter, which is the correct continuous-loop behaviour as-is.
  _goTo(index, animate, nearest) {
    let t = index;
    if (this.loop) {
      if (nearest) {
        let delta = (index - this.current) % this.n;
        if (delta > this.n / 2) delta -= this.n;
        else if (delta < -this.n / 2) delta += this.n;
        t = this.current + delta;
      }
    } else {
      t = Math.min(Math.max(index, 0), this.n - 1);
    }
    this.target = t;
    this.mode = 'snap';
    this.velocity = 0;
    if (animate === false || this.rmq.matches) {
      this.current = t;
      this._settle();
      this._render();
    } else {
      this._setWillChange(true);
      this._startAnim();
    }
  }

  // ---------- animation loop (momentum + snap) ----------
  _startAnim() {
    if (this.raf) return;
    const step = () => {
      this.raf = 0;
      let alive = true;

      if (this.mode === 'momentum') {
        this.current += this.velocity;
        this.velocity *= (+this.cfg.friction || 0.9);
        if (!this.loop) {
          if (this.current <= 0) { this.current = 0; this.velocity = 0; }
          if (this.current >= this.n - 1) { this.current = this.n - 1; this.velocity = 0; }
        }
        if (Math.abs(this.velocity) < 0.06) {
          // hand off to snap: aim at the slide the throw was heading toward
          let t = Math.round(this.current);
          if (!this.loop) t = Math.min(Math.max(t, 0), this.n - 1);
          this.target = t;
          this.mode = 'snap';
        }
      } else if (this.mode === 'snap') {
        const diff = this.target - this.current;
        this.current += diff * 0.2;
        if (Math.abs(diff) < 0.001) {
          this.current = this.target;
          alive = false;
        }
      } else {
        alive = false;
      }

      this._render();
      if (alive) {
        this.raf = requestAnimationFrame(step);
      } else {
        this._settle();
      }
    };
    this.raf = requestAnimationFrame(step);
  }

  _stopAnim() {
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = 0; }
  }

  // normalise unbounded loop counters back into range and drop will-change on idle
  _settle() {
    this.mode = 'idle';
    this.velocity = 0;
    if (this.loop) {
      this.current = this._wrapIndex(Math.round(this.current));
      this.target = this.current;
    }
    this._setWillChange(false);
  }

  _setWillChange(on) {
    this.refs.track.classList.toggle('cf-track--anim', on);
  }

  // ---------- pointer drag ----------
  _bind() {
    // prev = previous slide, next = next slide, regardless of dir: the arrows are positioned
    // with logical props and their glyphs mirrored in CSS, and the visual motion is mirrored
    // in the render via dirSign, so no index swap is needed here.
    this.prevBtns.forEach((b) => b.addEventListener('click', () => { this._userInteracted(); this._prev(); }));
    this.nextBtns.forEach((b) => b.addEventListener('click', () => { this._userInteracted(); this._next(); }));

    const vp = this.refs.viewport || this;
    vp.addEventListener('pointerdown', this._onDown = (e) => this._down(e));
    vp.addEventListener('pointermove', this._onMove = (e) => this._move(e));
    vp.addEventListener('pointerup', this._onUp = (e) => this._up(e));
    vp.addEventListener('pointercancel', this._onUp);
    vp.addEventListener('lostpointercapture', this._onUp);
    // don't let the browser turn a horizontal drag into a scroll/gesture
    vp.style.touchAction = 'pan-y';

    this.addEventListener('keydown', (e) => this._key(e));

    // desktop cursor tilt on the active card
    if (this.cfg.tilt && this.finePointer.matches) {
      vp.addEventListener('pointermove', (e) => this._tilt(e));
      vp.addEventListener('pointerleave', () => this._resetTilt());
    }

    // pause autoplay on hover / focus / tab-away
    this.addEventListener('pointerenter', () => this._pause('hover'));
    this.addEventListener('pointerleave', () => this._resume('hover'));
    this.addEventListener('focusin', () => this._pause('focus'));
    this.addEventListener('focusout', () => this._resume('focus'));
    this._bindWinDoc();
  }

  // document/window listeners + RO: (re)bound per connect, removed on disconnect — an editor
  // drag-move used to leave the slider permanently dead (no autoplay/re-measure) because
  // _init never resets while disconnectedCallback tore these down (2026-08-01 audit)
  _bindWinDoc() {
    if (this._winDocBound) return;
    this._winDocBound = true;
    this._onVis = this._onVis || (() => (document.hidden ? this._pause('tab') : this._resume('tab')));
    document.addEventListener('visibilitychange', this._onVis);

    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(() => {
        clearTimeout(this._rt);
        this._rt = setTimeout(() => { this._measure(); this._render(); }, 120);
      });
      this._ro.observe(this.refs.track);
    } else {
      this._onResize = this._onResize || (() => { this._measure(); this._render(); });
      window.addEventListener('resize', this._onResize);
    }
  }

  _down(e) {
    if (e.button != null && e.button !== 0) return;
    if (e.target.closest('a,button,[data-cf-dots],[data-cf-thumbs]')) {
      // a press that starts ON a link/button is never a drag — clear any residue from the
      // previous drag, else the slide click-handler preventDefaults this NEW click and the
      // link only works "sometimes" (owner-reported 2026-07-25)
      this._moved = false;
      return;
    }
    this._stopAnim();
    this.mode = 'drag';
    this.dragId = e.pointerId;
    try { (this.refs.viewport || this).setPointerCapture(e.pointerId); } catch (err) {}
    this._startX = e.clientX;
    this._startCurrent = this.current;
    this._moved = false;
    this._samples = [{ t: performance.now(), x: e.clientX }];
    this._setWillChange(true);
    this._userInteracted();
  }

  _move(e) {
    if (this.mode !== 'drag' || e.pointerId !== this.dragId) return;
    const dx = e.clientX - this._startX;
    if (Math.abs(dx) > (+this.cfg.dragThreshold || 6)) this._moved = true;
    // drag toward the finger: moving right (LTR) reveals the previous slide
    this.current = this._startCurrent - (dx / this.geom.step) * this.dirSign;
    if (!this.loop) this.current = Math.min(Math.max(this.current, -0.15), this.n - 1 + 0.15);
    this._samples.push({ t: performance.now(), x: e.clientX });
    if (this._samples.length > 6) this._samples.shift();
    this._scheduleRender();
  }

  _up(e) {
    if (this.mode !== 'drag' || (e.pointerId != null && e.pointerId !== this.dragId)) return;
    try { (this.refs.viewport || this).releasePointerCapture(this.dragId); } catch (err) {}
    this.dragId = null;
    // the drag's own terminating click (fires synchronously after pointerup) must still see
    // _moved=true; anything later must not — clear it on the next task
    if (this._moved) setTimeout(() => { this._moved = false; }, 0);

    // velocity in slides-per-frame from the last samples
    let v = 0;
    const s = this._samples;
    if (s && s.length >= 2) {
      const a = s[0], b = s[s.length - 1];
      const dt = Math.max(1, b.t - a.t);
      const pxPerMs = (b.x - a.x) / dt;
      v = -(pxPerMs * 16) / this.geom.step * this.dirSign;
    }
    v = Math.max(-2.5, Math.min(2.5, v));

    if (this.rmq.matches) {
      this._goTo(Math.round(this.current), false);
      return;
    }
    if (Math.abs(v) > 0.08) {
      this.velocity = v;
      this.mode = 'momentum';
    } else {
      this.target = Math.round(this.current);
      if (!this.loop) this.target = Math.min(Math.max(this.target, 0), this.n - 1);
      this.mode = 'snap';
    }
    this._startAnim();
  }

  _key(e) {
    const k = e.key;
    let handled = true;
    if (k === 'ArrowRight') this.rtl ? this._prev() : this._next();
    else if (k === 'ArrowLeft') this.rtl ? this._next() : this._prev();
    else if (k === 'Home') this._goTo(0, true, true);
    else if (k === 'End') this._goTo(this.n - 1, true, true);
    else handled = false;
    if (handled) { e.preventDefault(); this._userInteracted(); }
  }

  // ---------- tilt ----------
  _tilt(e) {
    if (this.mode === 'drag' || this.rmq.matches) return;
    const slide = this.slides[this.activeIndex];
    if (!slide || !slide._tilt) return;
    const r = slide.getBoundingClientRect();
    if (r.width < 4) return;
    const nx = (e.clientX - r.left) / r.width - 0.5;
    const ny = (e.clientY - r.top) / r.height - 0.5;
    if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) {
      this._resetTilt();
      return;
    }
    const max = +this.cfg.tiltMax || 8;
    if (this._tiltEl && this._tiltEl !== slide._tilt) this._tiltEl.style.transform = '';
    this._tiltEl = slide._tilt;
    slide._tilt.style.transform = `rotateX(${(-ny * max).toFixed(2)}deg) rotateY(${(nx * max).toFixed(2)}deg)`;
  }

  _resetTilt() {
    if (this._tiltEl) { this._tiltEl.style.transform = ''; this._tiltEl = null; }
  }

  // ---------- autoplay ----------
  _startAutoplay() {
    if (!this.cfg.autoplay || this.n < 2 || this.rmq.matches) return;
    this.autoplayOn = true;
    this.interval = Math.max(1200, +this.cfg.interval || 5000);
    this._apLast = performance.now();
    this._apElapsed = 0;
    const tick = (now) => {
      this.apRaf = requestAnimationFrame(tick);
      if (this._pauseReasons.size > 0) { this._apLast = now; return; }
      this._apElapsed += now - this._apLast;
      this._apLast = now;
      const frac = Math.min(1, this._apElapsed / this.interval);
      if (this.refs.progress) this.refs.progress.style.transform = `scaleX(${frac.toFixed(3)})`;
      if (this._apElapsed >= this.interval) {
        this._apElapsed = 0;
        // non-loop mode has no "next" past the last slide (_next clamps there); loop the
        // autoplay itself by wrapping back to the first slide instead of stalling
        if (!this.loop && this.activeIndex >= this.n - 1) this._goTo(0, true);
        else this._next();
      }
    };
    this.apRaf = requestAnimationFrame(tick);
  }

  _teardownAutoplay() {
    if (this.apRaf) { cancelAnimationFrame(this.apRaf); this.apRaf = 0; }
  }

  _pause(reason) {
    if (!this.autoplayOn) return;
    this._pauseReasons.add(reason);
  }

  _resume(reason) {
    this._pauseReasons.delete(reason);
    this._apLast = performance.now();
  }

  // a manual interaction resets the autoplay timer so it doesn't fire mid-gesture
  _userInteracted() {
    this._apElapsed = 0;
    this._apLast = performance.now();
    if (this.refs.progress) this.refs.progress.style.transform = 'scaleX(0)';
  }
}

if (!customElements.get('coverflow-slider')) {
  customElements.define('coverflow-slider', CoverflowSlider);
}
